import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { Subject, takeUntil } from "rxjs";
import { IExposedOrganizationSummary } from "src/app/epicare-public/administration/domain/interfaces/organization.interface";
import { OrganizationService } from "src/app/epicare-public/administration/domain/services/organization.service";
import { IOrganizationAccess } from "../../interfaces/access.interface";
import { ConnectionEventsService } from "../../services/connection-events.service";
import { PermissionLoaderService } from "../../services/permission-loader.service";
import { SessionService } from "../../services/session.service";

@Component({
  selector: "app-organization-switcher",
  templateUrl: "./organization-switcher.component.html",
  styleUrls: ["./organization-switcher.component.scss"],
})
export class OrganizationSwitcherComponent implements OnInit, OnDestroy {
  /** Prefix of each organization item's element id, keeping ids unique when several switchers are displayed */
  @Input() public idPrefix = "organization-switcher-orgId-";
  public orgs: IExposedOrganizationSummary[] | null = [];
  private onDestroy$ = new Subject<void>();

  constructor(
    public sessionService: SessionService,
    private organizationService: OrganizationService,
    private connectionEventsService: ConnectionEventsService,
    private permissionLoaderService: PermissionLoaderService,
    private translateService: TranslateService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Loads the user's organizations, and reloads them whenever the login state changes.
   */
  ngOnInit(): void {
    this.loadOrganizations();

    this.sessionService.onLoginStateChanged$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.loadOrganizations();
    });
  }

  /**
   * Emits onDestroy$ to terminate all active subscriptions.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Fetches the organizations the current user belongs to, or clears them if the user is not logged in.
   */
  private loadOrganizations(): void {
    if (!this.sessionService.isLoggedIn) {
      this.orgs = [];
      return;
    }

    this.organizationService
      .getMyOrganizations()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (res) => {
          this.orgs = res;
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
            duration: 5000,
          });
        },
      });
  }

  /**
   * @param org Organization to check
   * @return Whether the given organization is the one the user is currently connected to
   */
  public isCurrentOrg(org: IExposedOrganizationSummary): boolean {
    return org.id === this.sessionService.currentOrgId;
  }

  /**
   * Switches the current session to the given organization and reloads the page once the new
   * tokens and permissions have been received.
   * @param org Organization to switch to
   */
  public onOrgChange(org: IExposedOrganizationSummary): void {
    if (this.isCurrentOrg(org)) {
      return;
    }

    this.organizationService
      .changeMyOrganization(org.id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (value: IOrganizationAccess) => {
          if (value.tokens.accessToken && value.tokens.refreshToken) {
            this.connectionEventsService.notifyLogin({ ...value, organizationId: org.id });
            // The permissions just received are up to date: no need to fetch them again once reloaded
            this.permissionLoaderService.markUpToDateForReload();
            this.reloadPage();
          }
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
            duration: 5000,
          });
        },
      });
  }

  /**
   * Reloads the current page.
   */
  public reloadPage(): void {
    window.location.reload();
  }
}
